import * as CookieConsent from "vanilla-cookieconsent";
import type { PrivacyController } from "./controller.js";
import type { PrivacySnapshot } from "./types.js";

function revisionNumber(policyVersion: string): number {
  let hash = 0;
  for (const character of policyVersion) {
    hash = (Math.imul(hash, 31) + character.charCodeAt(0)) | 0;
  }
  return Math.abs(hash);
}

export async function initializeCookieConsentAdapter(
  controller: PrivacyController,
): Promise<void> {
  if (controller.config.mode !== "prior-consent") {
    throw new Error("CookieConsent adapter is only available in prior-consent mode");
  }
  const servicesFor = (category: "statistics" | "functionality") =>
    Object.fromEntries(
      controller.config.services
        .filter((service) => service.category === category)
        .map((service) => [service.id, { label: service.displayName }]),
    );
  await CookieConsent.run({
    mode: "opt-in",
    autoShow: false,
    disablePageInteraction: false,
    manageScriptTags: false,
    autoClearCookies: false,
    revision: revisionNumber(controller.config.policyVersion),
    cookie: {
      name: "miko_privacy_consent",
      expiresAfterDays: controller.config.preferenceLifetimeDays,
      sameSite: "Lax",
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      statistics: { services: servicesFor("statistics") },
      functionality: { services: servicesFor("functionality") },
    },
    language: {
      default: "en",
      translations: {
        en: {
          consentModal: {
            title: "Privacy choices",
            description: "Use the privacy page to choose optional technology.",
          },
          preferencesModal: {
            title: "Privacy choices",
            sections: [],
          },
        },
      },
    },
  });
}

export function syncCookieConsent(snapshot: PrivacySnapshot): void {
  if (snapshot.mode !== "prior-consent") return;
  const accepted = ["necessary"];
  for (const category of ["statistics", "functionality"] as const) {
    if (
      snapshot.services.some(
        (service) => service.category === category && service.state === "on",
      )
    ) {
      accepted.push(category);
    }
  }
  CookieConsent.acceptCategory(accepted);
  for (const category of ["statistics", "functionality"] as const) {
    const serviceIds = snapshot.services
      .filter((service) => service.category === category && service.state === "on")
      .map((service) => service.id);
    CookieConsent.acceptService(serviceIds, category);
  }
}
